import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useRef, useState, type DragEvent, type PointerEvent as ReactPointerEvent } from 'react';
import { clampDrag, clampRect, defaultRect, resizeRect, zoneAt, zoneRect, type Rect, type Zone } from '../core/geometry';
import { snapPreview, useApp, useSession, useUi, WindowContext } from '../app/context';
import { ui } from '../app/ui';
import { isReduced, OPTIC, play, RECEDE, setGesture, Spring } from '../motion/motion';
import { Head } from './Head';
import { Surface } from './Surface';
import { Pill } from './Pill';
import { SelectionPill } from './SelectionPill';
import { NoticeLine, Sheet } from './Overlays';
import { AlertIcon, TrayIcon } from './icons';
import { GripArc } from './Grip';
import { dragVerdict } from '../platform/files';

/**
 * A MyTXT window: one editor object on the canvas.
 *
 * Geometry is applied imperatively. While a window is dragged or resized it
 * follows the pointer exactly — no easing against the hand. Everything else
 * (release into a snap zone, maximise, restore, tidy, adapting to a resized
 * browser) is driven by a spring that starts from wherever the window is,
 * carrying the momentum of a throw into the settle.
 */

interface WindowGestures {
  /** Begin moving the window from a pointer press on its head. */
  startMove(e: ReactPointerEvent): void;
}

const GestureContext = createContext<WindowGestures | null>(null);

export function useWindowGestures(): WindowGestures {
  const g = useContext(GestureContext);
  if (!g) throw new Error('Window gestures missing');
  return g;
}

type Dir = { x: -1 | 0 | 1; y: -1 | 0 | 1 };
const HANDLES: Array<{ name: string; dir: Dir }> = [
  { name: 'n', dir: { x: 0, y: -1 } },
  { name: 's', dir: { x: 0, y: 1 } },
  { name: 'e', dir: { x: 1, y: 0 } },
  { name: 'w', dir: { x: -1, y: 0 } },
  { name: 'ne', dir: { x: 1, y: -1 } },
  { name: 'nw', dir: { x: -1, y: -1 } },
  { name: 'sw', dir: { x: -1, y: 1 } },
  { name: 'se', dir: { x: 1, y: 1 } },
];

/** Below this width the full toolbar no longer fits beside its margins; list tools fold into the style menu. */
const NARROW_WINDOW = 512;

const toArray = (r: Rect) => [r.x, r.y, r.w, r.h];
const toRect = (v: number[]): Rect => ({ x: v[0], y: v[1], w: v[2], h: v[3] });

/** Windows present when the canvas first renders take part in the entrance; later ones materialise. */
let canvasMounted = false;

export function markCanvasMounted() {
  canvasMounted = true;
}

export function Window({ id }: { id: string }) {
  const app = useApp();
  const win = useSession((s) => s.windows.find((w) => w.id === id)!);
  const focused = useSession((s) => s.focusedId === id);
  const single = useSession((s) => s.windows.length === 1);
  const compact = useUi((s) => s.compact);
  const ref = useRef<HTMLElement>(null);
  const frame = useRef<HTMLDivElement>(null);
  const spring = useRef<Spring | null>(null);
  const gesture = useRef<'move' | 'resize' | null>(null);
  const velocity = useRef<number[] | null>(null);
  const [narrow, setNarrow] = useState(false);
  const [dropping, setDropping] = useState<'files' | 'reject' | null>(null);
  const dragDepth = useRef(0);
  const [lifted, setLifted] = useState(false);

  const apply = useCallback((v: number[]) => {
    const el = ref.current;
    if (!el) return;
    el.style.transform = `translate3d(${Math.round(v[0])}px, ${Math.round(v[1])}px, 0)`;
    el.style.width = `${Math.round(v[2])}px`;
    el.style.height = `${Math.round(v[3])}px`;
  }, []);

  // Geometry: first placement is immediate; later changes spring from where the window is now.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (compact) {
      spring.current?.stop();
      el.style.transform = el.style.width = el.style.height = '';
      spring.current = null;
      return;
    }
    const target = toArray(win.rect);
    if (!spring.current) {
      spring.current = new Spring(target, apply, 330, 36);
      apply(target);
      return;
    }
    if (gesture.current) return;
    spring.current.to(target, { velocity: velocity.current ?? undefined });
    velocity.current = null;
  }, [win.rect, compact, apply]);

  useEffect(() => () => spring.current?.stop(), []);

  // Arrival. The windows of a page load take part in the opening; later windows materialise quickly.
  useLayoutEffect(() => {
    const el = frame.current;
    if (!el || !ui.get().ready) return;
    // Arrive once per element (development re-runs effects on the same element).
    if (el.dataset.arrived) return;
    el.dataset.arrived = 'true';
    if (!canvasMounted) {
      // Each load's windows arrive as part of the opening (see motion/intro.ts).
      if (ui.get().intro !== 'none') return;
      play(el, [{ opacity: 0, transform: 'translateY(8px) scale(0.99)', filter: 'blur(4px)' }, { opacity: 1, transform: 'none', filter: 'blur(0px)' }], { duration: 560, easing: OPTIC });
      return;
    }
    play(el, [{ opacity: 0, transform: 'translateY(10px) scale(0.965)', filter: 'blur(6px)' }, { opacity: 1, transform: 'none', filter: 'blur(0px)' }], { duration: 520, easing: OPTIC });
  }, []);

  // Leaving: a still image of the window recedes after the live one is gone.
  useLayoutEffect(() => {
    const el = ref.current;
    return () => {
      const parent = el?.parentElement;
      if (!el || !parent || isReduced() || ui.get().compact) return;
      const ghost = el.cloneNode(true) as HTMLElement;
      // Only a real removal leaves (development re-mounts keep the element connected).
      requestAnimationFrame(() => {
        if (el.isConnected || !parent.isConnected) return;
        ghost.setAttribute('aria-hidden', 'true');
        ghost.setAttribute('inert', '');
        ghost.classList.add('win-ghost');
        parent.appendChild(ghost);
        const body = ghost.querySelector('.win-frame');
        play(body, [{ opacity: 1, transform: 'none', filter: 'blur(0px)' }, { opacity: 0, transform: 'translateY(6px) scale(0.96)', filter: 'blur(5px)' }], { duration: 240, easing: RECEDE }).then(() => ghost.remove());
      });
    };
  }, []);

  // The toolbar folds its list tools away in a narrow window.
  useLayoutEffect(() => {
    const el = frame.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => setNarrow(entry.contentRect.width < NARROW_WINDOW));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const current = () => (spring.current ? toRect(spring.current.current) : win.rect);

  /** Track pointer velocity (px/s), lightly smoothed, for a released window's momentum. */
  const tracker = () => {
    let last = { x: 0, y: 0, t: 0 };
    let v = { x: 0, y: 0 };
    return {
      reset(x: number, y: number) {
        last = { x, y, t: performance.now() };
        v = { x: 0, y: 0 };
      },
      push(x: number, y: number) {
        const t = performance.now();
        const dt = Math.max(1, t - last.t) / 1000;
        v = { x: v.x * 0.6 + ((x - last.x) / dt) * 0.4, y: v.y * 0.6 + ((y - last.y) / dt) * 0.4 };
        last = { x, y, t };
      },
      get: () => (performance.now() - last.t > 80 ? { x: 0, y: 0 } : v),
    };
  };

  const startMove = (e: ReactPointerEvent) => {
    if (e.button !== 0 || compact) return;
    const el = ref.current;
    if (!el) return;
    app.focusWindow(id);
    const pointerId = e.pointerId;
    const target = e.currentTarget as HTMLElement;
    const origin = { x: e.clientX, y: e.clientY };
    let start = current();
    const startWin = app.session.get().windows.find((w) => w.id === id)!;
    let zone: Zone | null = null;
    let moved = false;
    const speed = tracker();
    speed.reset(e.clientX, e.clientY);

    const move = (ev: PointerEvent) => {
      const dx = ev.clientX - origin.x;
      const dy = ev.clientY - origin.y;
      if (!moved) {
        if (Math.hypot(dx, dy) < 4) return;
        moved = true;
        gesture.current = 'move';
        spring.current?.stop();
        target.setPointerCapture?.(pointerId);
        setLifted(true);
        setGesture('move');
        // Pulling a docked window away gives back its floating size, under the same point of the head.
        if (startWin.zone) {
          const restore = startWin.restore ?? defaultRect(app.canvas());
          const ratio = (origin.x - start.x) / start.w;
          start = { x: origin.x - restore.w * ratio, y: start.y, w: restore.w, h: restore.h };
        }
      }
      speed.push(ev.clientX, ev.clientY);
      const canvas = app.canvas();
      const next = clampDrag({ ...start, x: start.x + dx, y: start.y + dy }, canvas);
      spring.current?.set(toArray(next));
      zone = ui.get().compact ? null : zoneAt({ x: ev.clientX, y: ev.clientY }, canvas, canvas.w < 1100 ? 22 : 14);
      snapPreview.set(zone ? { rect: zoneRect(zone, canvas), zone } : null);
    };

    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
      snapPreview.set(null);
      setGesture(null);
      if (!moved) return;
      setLifted(false);
      gesture.current = null;
      const v = speed.get();
      velocity.current = [v.x, v.y, 0, 0];
      const now = current();
      const canvas = app.canvas();
      app.windowPlacedByHand();
      if (zone) {
        const floating = startWin.zone ? (startWin.restore ?? clampRect(now, canvas)) : startWin.rect;
        app.placeWindow(id, zoneRect(zone, canvas), zone, floating);
      } else {
        const settled = clampRect(now, canvas);
        app.placeWindow(id, settled, null, null);
        // Already exactly there: nothing left to animate.
        if (settled.x === Math.round(now.x) && settled.y === Math.round(now.y)) velocity.current = null;
        else spring.current?.to(toArray(settled), { velocity: velocity.current ?? undefined });
      }
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
  };

  const startResize = (dir: Dir, e: ReactPointerEvent) => {
    if (e.button !== 0 || compact) return;
    e.preventDefault();
    e.stopPropagation();
    app.focusWindow(id);
    const handle = e.currentTarget as HTMLElement;
    handle.setPointerCapture?.(e.pointerId);
    const origin = { x: e.clientX, y: e.clientY };
    const start = current();
    gesture.current = 'resize';
    spring.current?.stop();
    setGesture(`resize-${dir.x}${dir.y}`);
    const move = (ev: PointerEvent) => {
      spring.current?.set(toArray(resizeRect(start, dir, ev.clientX - origin.x, ev.clientY - origin.y, app.canvas())));
    };
    const up = () => {
      handle.removeEventListener('pointermove', move);
      handle.removeEventListener('pointerup', up);
      handle.removeEventListener('pointercancel', up);
      setGesture(null);
      gesture.current = null;
      app.windowPlacedByHand();
      app.placeWindow(id, clampRect(current(), app.canvas()), null, null);
    };
    handle.addEventListener('pointermove', move);
    handle.addEventListener('pointerup', up);
    handle.addEventListener('pointercancel', up);
  };

  /**
   * Files can be dropped anywhere on a window — its text, its head, its empty
   * page. Enter and leave fire for every child crossed, so depth is counted;
   * text and tab drags are not file drags and pass through untouched.
   */
  const drag = {
    onDragEnter: (e: DragEvent) => {
      const verdict = dragVerdict(e.dataTransfer);
      if (verdict === 'none') return;
      dragDepth.current++;
      setDropping(verdict);
    },
    onDragOver: (e: DragEvent) => {
      const verdict = dragVerdict(e.dataTransfer);
      if (verdict === 'none') return;
      e.preventDefault();
      e.dataTransfer.dropEffect = verdict === 'files' ? 'copy' : 'none';
      if (dropping !== verdict) setDropping(verdict);
    },
    onDragLeave: (e: DragEvent) => {
      if (dragVerdict(e.dataTransfer) === 'none') return;
      dragDepth.current = Math.max(0, dragDepth.current - 1);
      if (dragDepth.current === 0 || !e.currentTarget.contains(e.relatedTarget as Node)) {
        dragDepth.current = 0;
        setDropping(null);
      }
    },
    onDrop: (e: DragEvent) => {
      if (dragVerdict(e.dataTransfer) === 'none') return;
      e.preventDefault();
      e.stopPropagation();
      dragDepth.current = 0;
      setDropping(null);
      app.openDropped(e.dataTransfer, { windowId: id });
    },
  };

  // A drag that ends anywhere (Escape, a drop elsewhere) leaves no trace on the window.
  useEffect(() => {
    if (!dropping) return;
    const reset = () => {
      dragDepth.current = 0;
      setDropping(null);
    };
    window.addEventListener('dragend', reset);
    window.addEventListener('drop', reset);
    return () => {
      window.removeEventListener('dragend', reset);
      window.removeEventListener('drop', reset);
    };
  }, [dropping]);

  const gestures: WindowGestures = { startMove };

  return (
    <WindowContext.Provider value={id}>
      <GestureContext.Provider value={gestures}>
        <section
          ref={ref}
          className="win"
          data-focused={focused || undefined}
          data-zone={win.zone ?? undefined}
          data-lifted={lifted || undefined}
          data-dropping={dropping ?? undefined}
          data-single={single || undefined}
          data-window-id={id}
          style={{ zIndex: win.z }}
          aria-label="MyTXT window"
          onPointerDownCapture={() => !focused && app.focusWindow(id)}
          onFocusCapture={() => !focused && app.focusWindow(id)}
          {...drag}
        >
          <div className="win-frame" ref={frame}>
            <Head />
            <Surface />
            {focused && <NoticeLine />}
            <Pill compact={narrow} focused={focused} />
            {focused && <SelectionPill objectRef={frame} />}
            {focused && <Sheet />}
            <div className="drop-veil" aria-hidden="true">
              <span className="drop-badge">
                {dropping === 'reject' ? (
                  <>
                    <AlertIcon size={16} /> Only .md and .txt files open here
                  </>
                ) : (
                  <>
                    <TrayIcon size={16} /> Release to open in this window
                  </>
                )}
              </span>
            </div>
          </div>
          {!compact && win.zone !== 'max' && (
            <>
              {HANDLES.map((h) => (
                <div key={h.name} className={`win-resize win-resize-${h.name}`} aria-hidden="true" onPointerDown={(e) => startResize(h.dir, e)} />
              ))}
              <div className="win-grip" aria-hidden="true" onPointerDown={(e) => startResize({ x: 1, y: 1 }, e)}>
                <GripArc />
              </div>
            </>
          )}
        </section>
      </GestureContext.Provider>
    </WindowContext.Provider>
  );
}
